import { z } from "zod";
import { selectedPlaybookSchema } from "../agents/selectedPlaybook.js";
import { paragraphSchema } from "../helpers/gradeValidationHelpers.js";
import { normalizedNodeGradingStatuses } from "../helpers/normalizedNodeGradingHelpers.js";
import {
  copilotProposalIdSchema,
  playbookProposalKindSchema
} from "./copilotProposal.js";
import { outcomeToolOutputSchema } from "./outcome.js";
import { recordingIdSchema } from "./recording.js";
import { metadataFieldIdSchema } from "./signal.js";
import { gradingIdSchema } from "./teammatePerformanceSummary.js";
const CONFIGURATION_AGENT_ID = "configurationAgent";
const SIGNAL_CONFIGURATION_AGENT_ID = "signalConfigurationAgent";
const KNOWLEDGE_BASE_AGENT_ID = "knowledgeBaseAgent";
const LEARNING_JOURNEY_CONFIGURATION_AGENT_ID = "learningJourneyConfigurationAgent";
const criteriaTypeSchema = z.enum(["yes_no", "1_5_score"]);
const STATIC_AGENT_SECTION_LABELS = {
  agent_name: "Name",
  agent_role_and_personality: "Role and personality",
  caller_context: "Caller context",
  conversation_flow: "Conversation flow",
  behavior_and_guardrails: "General behavior and guardrails"
};
const getCustomerInteractionToolInputSchema = z.object({
  interaction_id: z.uuid().describe("Customer interaction ID (grading UUID) to retrieve details for")
});
const getCustomerInteractionToolOutputSchema = z.object({
  interaction: z.object({
    interaction_id: z.uuid(),
    transcript: z.string().describe("Full transcript text with speaker labels"),
    playbook_grading: z.string().describe("Formatted playbook grading results"),
    outcome: outcomeToolOutputSchema,
    feedback: z.object({
      sentiment: z.enum(["positive", "negative"]),
      commentary: z.string()
    }).nullable().describe("User feedback on the call")
  }).nullable().describe("Customer interaction details or null if not found")
});
const simulateAgentConversationToolInputSchema = z.object({
  scenario: z.string().describe(
    "1-4 sentence description of the scenario to simulate, focusing on the customer's behavior (not the agent's behavior)"
  )
});
const transcriptMessageSchema = z.object({
  role: z.enum(["user", "assistant", "tool"]),
  content: z.string(),
  toolInput: z.string().nullable()
});
const simulateAgentConversationToolOutputSchema = z.object({
  transcript: z.array(transcriptMessageSchema),
  commentary: z.string().describe("2-4 sentence analysis of agent performance")
});
const simulateRolePlayRealismToolInputSchema = z.object({
  title: z.string().min(1).nullable().optional().describe("Optional title for the role-play lesson being simulated."),
  participant_instructions: z.string().min(1).describe(
    "Learner-facing role-play instructions to simulate. Pass the exact instructions from the current lesson or proposed draft."
  ),
  agent_instructions: z.string().min(1).describe(
    "AI role-play agent instructions to simulate. Pass the exact instructions from the current lesson or proposed draft."
  ),
  source_interaction_id: gradingIdSchema.nullable().optional().describe(
    "ID of the real customer interaction this role play was modeled on, from the user or from customer-interaction research. Expected for new role plays - omit only when research found no suitable source interaction or the user asked for an invented scenario."
  )
});
const simulatedRolePlayTranscriptMessageSchema = z.object({
  role: z.enum(["participant", "role_play_agent"]),
  content: z.string()
});
const simulatedRolePlayAssessmentFindingSchema = z.object({
  commentary: z.string().describe(
    "A succinct description of the issue observed, referencing the relevant moment in the conversation. No verbatim transcript quotes."
  )
});
const simulatedRolePlayAssessmentSchema = z.object({
  findings: z.array(simulatedRolePlayAssessmentFindingSchema).nullable().describe(
    "Configuration problems revealed by the transcript, most significant first. Use null when the configuration held up well."
  ),
  summary: z.string().describe(
    "3-5 sentence overall assessment of how realistically the role-play AI agent behaved, how well the lesson configuration held up, and any recommended instruction changes. If no changes are needed, say so plainly."
  )
});
const simulateRolePlayRealismToolOutputSchema = z.object({
  transcript: z.array(simulatedRolePlayTranscriptMessageSchema),
  // Optional so older persisted tool outputs (transcript-only) still parse.
  assessment: simulatedRolePlayAssessmentSchema.optional()
});
function normalizeOptionalToolField(value) {
  if (value === null) {
    return void 0;
  }
  if (typeof value === "string" && value.trim() === "") {
    return void 0;
  }
  return value;
}
const optionalCriteriaTypeToolFieldSchema = z.preprocess(
  normalizeOptionalToolField,
  criteriaTypeSchema.optional()
);
const requiredUuidToolFieldSchema = z.preprocess(
  normalizeOptionalToolField,
  z.uuid()
);
const optionalUuidToolFieldSchema = z.preprocess(
  normalizeOptionalToolField,
  z.uuid().optional()
);
const optionalNonEmptyStringToolFieldSchema = z.preprocess(
  normalizeOptionalToolField,
  z.string().min(1).optional()
);
const criterionProposalDraftSchema = z.object({
  criteriaType: optionalCriteriaTypeToolFieldSchema,
  name: optionalNonEmptyStringToolFieldSchema,
  description: optionalNonEmptyStringToolFieldSchema.describe(
    "Only used for `section` targets (the section's description). Not used for criteria \u2014 omit it for `criterion` and `new_criterion`."
  ),
  gradingContext: optionalNonEmptyStringToolFieldSchema
}).refine(
  (draft) => !!(draft.criteriaType || draft.name || draft.description || draft.gradingContext),
  {
    message: "At least one updated criterion field is required"
  }
);
const criterionProposalDraftForSamplingSchema = z.object({
  criteriaType: optionalCriteriaTypeToolFieldSchema,
  name: optionalNonEmptyStringToolFieldSchema,
  gradingContext: optionalNonEmptyStringToolFieldSchema
}).refine(
  (draft) => !!(draft.criteriaType || draft.name || draft.gradingContext),
  { message: "At least one updated criterion field is required" }
);
const sectionProposalDraftSchema = z.object({
  name: optionalNonEmptyStringToolFieldSchema,
  description: optionalNonEmptyStringToolFieldSchema
}).refine((draft) => !!(draft.name || draft.description), {
  message: "At least one updated section field is required"
});
const outcomeProposalDraftSchema = z.object({
  context: optionalNonEmptyStringToolFieldSchema
}).refine((draft) => !!draft.context, {
  message: "candidate_draft.context is required"
});
const testCriterionProposalTargetTypeSchema = z.enum([
  "criterion",
  "section",
  "new_criterion"
]);
const testCriterionProposalToolInputSchema = z.object({
  target_type: testCriterionProposalTargetTypeSchema.describe(
    "Whether the target is an existing `criterion`, an existing `section`, or a brand-new `new_criterion` to be added under `parent_section_id` for testing."
  ),
  target_id: optionalUuidToolFieldSchema.describe(
    "Required when target_type is `criterion` or `section`. The existing playbook node ID. Must be omitted when target_type is `new_criterion`."
  ),
  parent_section_id: optionalUuidToolFieldSchema.describe(
    "Required when target_type is `new_criterion`. The existing section ID under which the proposed new criterion should be inserted for testing."
  ),
  candidate_draft: criterionProposalDraftSchema.describe(
    "Fields for the target node. For an existing criterion update, any subset of `name`, `criteriaType`, and `gradingContext` is allowed (do not propose `description` for criteria). For a section update, only `name` and `description` are allowed. For `new_criterion`, `name`, `criteriaType`, and `gradingContext` are required (do not propose `description`)."
  ),
  interaction_ids: z.array(gradingIdSchema).min(1).max(12).describe(
    "Explicit interaction IDs to evaluate for before/after comparison."
  )
}).superRefine((input, ctx) => {
  if (input.target_type === "criterion") {
    if (!input.target_id) {
      ctx.addIssue({
        code: "custom",
        message: "target_id is required when target_type is `criterion`",
        path: ["target_id"]
      });
    }
    if (input.parent_section_id) {
      ctx.addIssue({
        code: "custom",
        message: "parent_section_id is only used when target_type is `new_criterion`",
        path: ["parent_section_id"]
      });
    }
    if (input.candidate_draft.description) {
      ctx.addIssue({
        code: "custom",
        message: "description cannot be set when testing a criterion",
        path: ["candidate_draft", "description"]
      });
    }
    return;
  }
  if (input.target_type === "new_criterion") {
    if (input.target_id) {
      ctx.addIssue({
        code: "custom",
        message: "target_id must be omitted when target_type is `new_criterion`",
        path: ["target_id"]
      });
    }
    if (!input.parent_section_id) {
      ctx.addIssue({
        code: "custom",
        message: "parent_section_id is required when target_type is `new_criterion`",
        path: ["parent_section_id"]
      });
    }
    if (!input.candidate_draft.name) {
      ctx.addIssue({
        code: "custom",
        message: "candidate_draft.name is required for a new_criterion",
        path: ["candidate_draft", "name"]
      });
    }
    if (!input.candidate_draft.criteriaType) {
      ctx.addIssue({
        code: "custom",
        message: "candidate_draft.criteriaType is required for a new_criterion",
        path: ["candidate_draft", "criteriaType"]
      });
    }
    if (!input.candidate_draft.gradingContext) {
      ctx.addIssue({
        code: "custom",
        message: "candidate_draft.gradingContext is required for a new_criterion",
        path: ["candidate_draft", "gradingContext"]
      });
    }
    if (input.candidate_draft.description) {
      ctx.addIssue({
        code: "custom",
        message: "description cannot be set when proposing a new criterion",
        path: ["candidate_draft", "description"]
      });
    }
    return;
  }
  if (!input.target_id) {
    ctx.addIssue({
      code: "custom",
      message: "target_id is required when target_type is `section`",
      path: ["target_id"]
    });
  }
  if (input.parent_section_id) {
    ctx.addIssue({
      code: "custom",
      message: "parent_section_id is only used when target_type is `new_criterion`",
      path: ["parent_section_id"]
    });
  }
  const sectionDraftResult = sectionProposalDraftSchema.safeParse(
    input.candidate_draft
  );
  if (!sectionDraftResult.success) {
    for (const issue of sectionDraftResult.error.issues) {
      ctx.addIssue({
        ...issue,
        path: ["candidate_draft", ...issue.path]
      });
    }
  }
  if (input.candidate_draft.criteriaType) {
    ctx.addIssue({
      code: "custom",
      message: "criteriaType cannot be updated when testing a section",
      path: ["candidate_draft", "criteriaType"]
    });
  }
  if (input.candidate_draft.gradingContext) {
    ctx.addIssue({
      code: "custom",
      message: "gradingContext cannot be updated when testing a section",
      path: ["candidate_draft", "gradingContext"]
    });
  }
});
const criterionProposalGradeSnapshotSchema = z.object({
  node_id: z.string(),
  node_type: z.enum(["criteria", "section"]),
  node_name: z.string(),
  status: z.enum([...normalizedNodeGradingStatuses, "ungraded"]),
  value: z.string().nullable(),
  commentary: z.string().nullable()
});
const criterionProposalValidationRollupSchema = z.object({
  score_distribution: z.record(z.string(), z.number()),
  applicable_count: z.number(),
  not_applicable_count: z.number(),
  insufficient_context_count: z.number(),
  ungraded_count: z.number(),
  graded_count: z.number()
});
const testCriterionProposalToolOutputSchema = z.object({
  evaluated_interactions: z.array(
    z.object({
      interaction_id: z.uuid(),
      teammate_name: z.string(),
      recorded_at: z.string(),
      before_grade: criterionProposalGradeSnapshotSchema,
      after_grade: criterionProposalGradeSnapshotSchema
    })
  ),
  before_rollup: criterionProposalValidationRollupSchema,
  after_rollup: criterionProposalValidationRollupSchema
});
const sampleCriteriaScoreDistributionTargetTypeSchema = z.enum([
  "criterion",
  "new_criterion"
]);
const sampleCriteriaScoreDistributionInputFields = {
  target_type: sampleCriteriaScoreDistributionTargetTypeSchema.describe(
    "Whether to analyze a rewrite of an existing `criterion` or a brand-new `new_criterion` under `parent_section_id`."
  ),
  target_id: optionalUuidToolFieldSchema.describe(
    "Required when target_type is `criterion`. The existing criterion node ID. Must be omitted when target_type is `new_criterion`."
  ),
  parent_section_id: optionalUuidToolFieldSchema.describe(
    "Required when target_type is `new_criterion`. The existing section ID under which the proposed criterion should be inserted for sampling."
  ),
  candidate_draft: criterionProposalDraftForSamplingSchema.describe(
    "Fields for the candidate criterion. For an existing criterion update, any subset of `name`, `criteriaType`, and `gradingContext` is allowed. For `new_criterion`, `name`, `criteriaType`, and `gradingContext` are required."
  ),
  date: z.object({
    after: z.iso.datetime({ offset: true }).nullish(),
    before: z.iso.datetime({ offset: true }).nullish()
  }).nullish().describe(
    "Optional recorded-at date range for deterministic most-recent sampling. Defaults to the last 365 days."
  )
};
function addSampleCriteriaScoreDistributionInputIssues(input, prior, ctx) {
  if (input.target_type === "criterion") {
    if (!input.target_id) {
      ctx.addIssue({
        code: "custom",
        message: "target_id is required when target_type is `criterion`",
        path: ["target_id"]
      });
    }
    if (input.parent_section_id) {
      ctx.addIssue({
        code: "custom",
        message: "parent_section_id is only used when target_type is `new_criterion`",
        path: ["parent_section_id"]
      });
    }
    if (prior.value) {
      ctx.addIssue({
        code: "custom",
        message: `${prior.fieldName} is only used when target_type is \`new_criterion\``,
        path: [prior.fieldName]
      });
    }
    return;
  }
  if (input.target_id) {
    ctx.addIssue({
      code: "custom",
      message: "target_id must be omitted when target_type is `new_criterion`",
      path: ["target_id"]
    });
  }
  if (!input.parent_section_id) {
    ctx.addIssue({
      code: "custom",
      message: "parent_section_id is required when target_type is `new_criterion`",
      path: ["parent_section_id"]
    });
  }
  if (!input.candidate_draft.name) {
    ctx.addIssue({
      code: "custom",
      message: "candidate_draft.name is required for a new_criterion",
      path: ["candidate_draft", "name"]
    });
  }
  if (!input.candidate_draft.criteriaType) {
    ctx.addIssue({
      code: "custom",
      message: "candidate_draft.criteriaType is required for a new_criterion",
      path: ["candidate_draft", "criteriaType"]
    });
  }
  if (!input.candidate_draft.gradingContext) {
    ctx.addIssue({
      code: "custom",
      message: "candidate_draft.gradingContext is required for a new_criterion",
      path: ["candidate_draft", "gradingContext"]
    });
  }
}
function criterionDraftChangesNode(criterion, draft) {
  if (draft.name !== void 0 && draft.name !== criterion.name) {
    return true;
  }
  if (draft.criteriaType !== void 0 && draft.criteriaType !== criterion.grading_value_type) {
    return true;
  }
  if (draft.gradingContext !== void 0 && draft.gradingContext !== (criterion.grading_context ?? "")) {
    return true;
  }
  if (draft.description !== void 0 && draft.description !== (criterion.description ?? "")) {
    return true;
  }
  return false;
}
function sectionDraftChangesNode(section, draft) {
  if (draft.name !== void 0 && draft.name !== section.name) {
    return true;
  }
  if (draft.description !== void 0 && draft.description !== (section.description ?? "")) {
    return true;
  }
  return false;
}
function outcomeDraftChangesRubric(outcome, draft) {
  return draft.context !== void 0 && draft.context !== outcome.context;
}
const playbookNodeTypeSchema = z.enum(["section", "criteria"]);
const playbookProposalActionSchema = z.enum([
  "create",
  "update",
  "delete"
]);
const playbookNodeDraftSchema = z.object({
  name: optionalNonEmptyStringToolFieldSchema,
  description: optionalNonEmptyStringToolFieldSchema.describe(
    "Only for a `section` \u2014 its applicability description. Omit for criteria."
  ),
  criteriaType: optionalCriteriaTypeToolFieldSchema.describe(
    "Only for a `criteria` \u2014 its grade scale. Omit for sections."
  ),
  gradingContext: optionalNonEmptyStringToolFieldSchema.describe(
    "Only for a `criteria` \u2014 its grading rubric. Omit for sections."
  )
});
function addProposePlaybookNodeInputIssues(input, ctx) {
  const { node_type, action, candidate_draft } = input;
  if (action === "create") {
    if (input.target_id) {
      ctx.addIssue({
        code: "custom",
        message: "target_id must be omitted when action is `create`",
        path: ["target_id"]
      });
    }
    if (node_type === "criteria" && !input.parent_section_id) {
      ctx.addIssue({
        code: "custom",
        message: "parent_section_id is required to create a criteria, naming the section it goes under",
        path: ["parent_section_id"]
      });
    }
  } else {
    if (!input.target_id) {
      ctx.addIssue({
        code: "custom",
        message: `target_id is required when action is \`${action}\``,
        path: ["target_id"]
      });
    }
    if (input.parent_section_id) {
      ctx.addIssue({
        code: "custom",
        message: "parent_section_id is only used when action is `create`",
        path: ["parent_section_id"]
      });
    }
    if (input.prior_proposal_id) {
      ctx.addIssue({
        code: "custom",
        message: "prior_proposal_id is only used when action is `create` \u2014 an existing node is already identified by its target_id",
        path: ["prior_proposal_id"]
      });
    }
  }
  if (action === "delete") {
    if (candidate_draft) {
      ctx.addIssue({
        code: "custom",
        message: "candidate_draft must be omitted when action is `delete`",
        path: ["candidate_draft"]
      });
    }
    return;
  }
  if (!candidate_draft) {
    ctx.addIssue({
      code: "custom",
      message: `candidate_draft is required when action is \`${action}\``,
      path: ["candidate_draft"]
    });
    return;
  }
  const allowed = node_type === "criteria" ? ["name", "criteriaType", "gradingContext"] : ["name", "description"];
  for (const field of [
    "name",
    "description",
    "criteriaType",
    "gradingContext"
  ]) {
    if (candidate_draft[field] === void 0) {
      continue;
    }
    if (!allowed.includes(field)) {
      ctx.addIssue({
        code: "custom",
        message: `candidate_draft.${field} is not a ${node_type} field`,
        path: ["candidate_draft", field]
      });
    }
  }
  if (!allowed.some((field) => candidate_draft[field] !== void 0)) {
    ctx.addIssue({
      code: "custom",
      message: `candidate_draft needs at least one ${node_type} field`,
      path: ["candidate_draft"]
    });
  }
  if (action === "create") {
    const required = node_type === "criteria" ? ["name", "criteriaType", "gradingContext"] : ["name"];
    for (const field of required) {
      if (!candidate_draft[field]) {
        ctx.addIssue({
          code: "custom",
          message: `candidate_draft.${field} is required to create a ${node_type}`,
          path: ["candidate_draft", field]
        });
      }
    }
  }
}
const proposePlaybookNodeToolInputSchema = z.object({
  node_type: playbookNodeTypeSchema.describe(
    "What the proposal is about: a `section` or a `criteria`."
  ),
  action: playbookProposalActionSchema.describe(
    "What to do with it: `create` a new one, `update` the existing one, or `delete` it."
  ),
  target_id: optionalUuidToolFieldSchema.describe(
    "The existing node's id. Required to `update` or `delete`; omitted to `create`."
  ),
  parent_section_id: optionalUuidToolFieldSchema.describe(
    "The section the new node goes under. Required to create a criteria; for a section, omit it to propose a top-level section. Only used with `create`."
  ),
  prior_proposal_id: optionalUuidToolFieldSchema.describe(
    "Only when refining a `create` proposal you made earlier this conversation: pass that call's `proposal_id` so it is superseded rather than duplicated. Omit when proposing a genuinely different node."
  ),
  candidate_draft: z.preprocess(
    normalizeOptionalToolField,
    playbookNodeDraftSchema.optional()
  ).describe(
    "The proposed node. Required to `create` or `update`; omitted to `delete`."
  ),
  date: z.object({
    after: z.iso.datetime({ offset: true }).nullish(),
    before: z.iso.datetime({ offset: true }).nullish()
  }).nullish().describe(
    "Optional recorded-at range for the verification sample. Defaults to the last 365 days."
  )
}).superRefine(addProposePlaybookNodeInputIssues);
const getSampleCriteriaScoreDistributionAndOutcomeCorrelationToolInputSchema = z.object({
  target_type: sampleCriteriaScoreDistributionInputFields.target_type,
  target_id: sampleCriteriaScoreDistributionInputFields.target_id,
  parent_section_id: sampleCriteriaScoreDistributionInputFields.parent_section_id,
  prior_proposal_id: optionalUuidToolFieldSchema.describe(
    "Only valid when target_type is `new_criterion`. When you are refining a new-criterion proposal you already sampled earlier this session, pass that prior call's returned `proposal_id` here so the existing proposal is superseded instead of creating a duplicate. Omit it when proposing a genuinely different new criterion."
  ),
  candidate_draft: sampleCriteriaScoreDistributionInputFields.candidate_draft,
  date: sampleCriteriaScoreDistributionInputFields.date
}).superRefine(
  (input, ctx) => addSampleCriteriaScoreDistributionInputIssues(
    input,
    { fieldName: "prior_proposal_id", value: input.prior_proposal_id },
    ctx
  )
);
const sampleSideMetricsSchema = z.object({
  score_distribution: z.record(z.string(), z.number()).describe(
    'Count of graded sample calls per score key \u2014 keys are "yes"/"no" for a yes_no criterion, or "1" through "5" for a 1_5_score criterion.'
  ),
  // Omitted when the sample window contains only one outcome color — the
  // conversion-rate math is degenerate in that case.
  conversion_rates_per_score: z.record(z.string(), z.number()).optional().describe(
    "Fraction (0\u20131) of calls at each score key that reached the green (converted) outcome. Omitted when the sampled window contained a single outcome color, where this would be degenerate."
  ),
  net_conversions: z.number().nullable().optional().describe(
    "Expected extra green outcomes vs. a non-predictive criterion. `null` when there are no scored calls; omitted in the degenerate single-outcome-color case."
  )
});
const playbookProposalResultSchema = z.object({
  proposal_id: copilotProposalIdSchema.describe(
    "The persisted copilot proposal id backing this suggestion (in-agent tool only; the MCP tool omits this field). Pass it as `prior_proposal_id` when refining a proposal for a node that has no id yet."
  ),
  playbook: selectedPlaybookSchema.describe(
    "The playbook this proposal targets, for the chat's proposal card and its editor deep-link."
  ),
  node_name: z.string().describe("The proposed node's name, for labelling the suggestion."),
  kind: playbookProposalKindSchema.describe(
    "What this proposal is about, which decides the card's heading and the operation the editor applies."
  )
});
const getSampleCriteriaScoreDistributionAndOutcomeCorrelationToolOutputSchema = playbookProposalResultSchema.extend({
  sample_size: z.number().describe(
    "Number of graded sample calls (with a non-N/A outcome) the metrics were computed from."
  ),
  grading_value_type: z.enum(["yes_no", "1_5_score"]).describe(
    "The criterion's grade scale, which determines the score keys in the distribution."
  ),
  baseline: sampleSideMetricsSchema.nullable().describe(
    "Metrics for the criterion as currently written (live). `null` for a brand-new criterion, which has no current version to compare against."
  ),
  tested: sampleSideMetricsSchema.describe(
    "Metrics for the candidate draft being evaluated."
  )
});
const criteriaHealthCheckDimensionSchema = z.object({
  status: z.enum(["pass", "fail"]),
  explanation: z.string().describe(
    "A concise explanation (1-3 sentences) of the pass/fail assessment."
  )
});
const playbookCriteriaHealthCheckLlmOutputSchema = z.object({
  grading_clarity: criteriaHealthCheckDimensionSchema,
  transcript_observability: criteriaHealthCheckDimensionSchema,
  outcome_causation: criteriaHealthCheckDimensionSchema.nullable(),
  score_distribution: criteriaHealthCheckDimensionSchema.nullable(),
  non_duplicative: criteriaHealthCheckDimensionSchema
});
const sampleSectionApplicabilityTargetTypeSchema = z.enum([
  "section",
  "new_section"
]);
const sampleSectionApplicabilityInputFields = {
  target_type: sampleSectionApplicabilityTargetTypeSchema.describe(
    "Whether to analyze a rewrite of an existing `section` or a brand-new `new_section` under `parent_section_id`."
  ),
  target_id: optionalUuidToolFieldSchema.describe(
    "Required when target_type is `section`. The existing section node ID. Must be omitted when target_type is `new_section`."
  ),
  parent_section_id: optionalUuidToolFieldSchema.describe(
    "Required when target_type is `new_section`. The existing section ID under which the proposed section should be inserted for sampling. Omit to propose a top-level section."
  ),
  candidate_draft: sectionProposalDraftSchema.describe(
    "Fields for the candidate section. Only `name` and `description` are allowed. For `new_section`, `name` is required."
  ),
  date: z.object({
    after: z.iso.datetime({ offset: true }).nullish(),
    before: z.iso.datetime({ offset: true }).nullish()
  }).nullish().describe(
    "Optional recorded-at date range for deterministic most-recent sampling. Defaults to the last 365 days."
  )
};
function addSampleSectionApplicabilityInputIssues(input, prior, ctx) {
  if (input.target_type === "section") {
    if (!input.target_id) {
      ctx.addIssue({
        code: "custom",
        message: "target_id is required when target_type is `section`",
        path: ["target_id"]
      });
    }
    if (input.parent_section_id) {
      ctx.addIssue({
        code: "custom",
        message: "parent_section_id is only used when target_type is `new_section`",
        path: ["parent_section_id"]
      });
    }
    if (prior.value) {
      ctx.addIssue({
        code: "custom",
        message: `${prior.fieldName} is only used when target_type is \`new_section\``,
        path: [prior.fieldName]
      });
    }
    return;
  }
  if (input.target_id) {
    ctx.addIssue({
      code: "custom",
      message: "target_id must be omitted when target_type is `new_section`",
      path: ["target_id"]
    });
  }
  if (!input.candidate_draft.name) {
    ctx.addIssue({
      code: "custom",
      message: "candidate_draft.name is required for a new_section",
      path: ["candidate_draft", "name"]
    });
  }
}
const getSampleSectionApplicabilityToolInputSchema = z.object({
  target_type: sampleSectionApplicabilityInputFields.target_type,
  target_id: sampleSectionApplicabilityInputFields.target_id,
  parent_section_id: sampleSectionApplicabilityInputFields.parent_section_id,
  prior_proposal_id: optionalUuidToolFieldSchema.describe(
    "Only valid when target_type is `new_section`. When refining a new-section proposal you already sampled earlier this session, pass that prior call's returned `proposal_id` here so the existing proposal is superseded instead of creating a duplicate. Omit it when proposing a genuinely different new section."
  ),
  candidate_draft: sampleSectionApplicabilityInputFields.candidate_draft,
  date: sampleSectionApplicabilityInputFields.date
}).superRefine(
  (input, ctx) => addSampleSectionApplicabilityInputIssues(
    input,
    { fieldName: "prior_proposal_id", value: input.prior_proposal_id },
    ctx
  )
);
const sampleSectionSideMetricsSchema = z.object({
  // Counts keyed by "applicable" / "not_applicable".
  score_distribution: z.record(z.string(), z.number())
});
const getSampleSectionApplicabilityToolOutputSchema = playbookProposalResultSchema.extend({
  sample_size: z.number(),
  baseline: sampleSectionSideMetricsSchema.nullable(),
  tested: sampleSectionSideMetricsSchema
});
const proposePlaybookNodeToolOutputSchema = playbookProposalResultSchema.extend({
  node_type: playbookNodeTypeSchema,
  action: playbookProposalActionSchema,
  sample_size: z.number().optional().describe(
    "Graded sample calls the verification ran against. Absent for a `delete`."
  ),
  grading_value_type: z.enum(["yes_no", "1_5_score"]).optional().describe(
    "The criteria's grade scale, which determines the score keys. Only for `criteria`."
  ),
  baseline: z.union([sampleSideMetricsSchema, sampleSectionSideMetricsSchema]).nullish().describe(
    "The node as it stands today. `null` when creating, which has nothing to compare against; absent for a `delete`."
  ),
  tested: z.union([sampleSideMetricsSchema, sampleSectionSideMetricsSchema]).optional().describe("The proposed node. Absent for a `delete`.")
});
const playbookSectionHealthCheckLlmOutputSchema = z.object({
  grading_clarity: criteriaHealthCheckDimensionSchema,
  transcript_observability: criteriaHealthCheckDimensionSchema,
  non_duplicative: criteriaHealthCheckDimensionSchema
});
const proposePlaybookOutcomeToolInputSchema = z.object({
  candidate_draft: outcomeProposalDraftSchema.describe(
    "Fields for the candidate outcome. Only `context` (the grading rubric) is allowed and it is required. The outcome's type and result values/labels cannot be changed."
  ),
  date: z.object({
    after: z.iso.datetime({ offset: true }).nullish(),
    before: z.iso.datetime({ offset: true }).nullish()
  }).nullish().describe(
    "Optional recorded-at date range for deterministic most-recent sampling. Defaults to the last 365 days."
  )
});
const sampleOutcomeSideMetricsSchema = z.object({
  // Counts keyed by outcome result value (e.g. "win_loss:won"), including the
  // N/A value.
  score_distribution: z.record(z.string(), z.number())
});
const proposePlaybookOutcomeToolOutputSchema = playbookProposalResultSchema.extend({
  sample_size: z.number(),
  baseline: sampleOutcomeSideMetricsSchema.nullable(),
  tested: sampleOutcomeSideMetricsSchema
});
const getPlaybookConfigurationInstructionsToolInputSchema = z.object({});
const getPlaybookConfigurationInstructionsToolOutputSchema = z.object({
  instructions: z.string().describe(
    "The playbook-configuration workflow plus the playbook's current draft configuration (tree with grading rubrics, outcome definition)."
  )
});
const playbookOutcomeHealthCheckLlmOutputSchema = z.object({
  grading_clarity: criteriaHealthCheckDimensionSchema,
  transcript_observability: criteriaHealthCheckDimensionSchema
});
const agentInstructionCheckSchema = z.object({
  status: z.enum(["pass", "fail"]),
  explanation: z.string().describe(
    "A concise explanation (1-3 sentences) of the pass/fail assessment."
  )
});
const agentInstructionChecksLlmOutputSchema = z.object({
  identity: z.object({
    name_and_role_clear: agentInstructionCheckSchema,
    tone_and_personality_clear: agentInstructionCheckSchema
  }),
  caller_context: z.object({
    caller_persona_defined: agentInstructionCheckSchema,
    consistent_with_conversation_flow: agentInstructionCheckSchema
  }),
  conversation_flow: z.object({
    opening_clear: agentInstructionCheckSchema,
    steps_well_sequenced: agentInstructionCheckSchema,
    ending_paths_defined: agentInstructionCheckSchema,
    instructions_consistent: agentInstructionCheckSchema,
    tool_usage_clear: agentInstructionCheckSchema
  }),
  behavior_and_guardrails: z.object({
    boundaries_defined: agentInstructionCheckSchema,
    unexpected_scenarios_handled: agentInstructionCheckSchema,
    guardrails_consistent: agentInstructionCheckSchema,
    instructions_not_duplicated: agentInstructionCheckSchema
  })
});
const agentInstructionSectionCandidateDraftSchema = z.object({
  agent_name: z.string(),
  agent_role_and_personality: z.string(),
  caller_context: z.string(),
  conversation_flow: z.string(),
  behavior_and_guardrails: z.string()
}).partial();
const getAgentInstructionChecksToolInputSchema = z.object({
  candidate_draft: agentInstructionSectionCandidateDraftSchema.nullish().describe(
    "Optional proposed section content to test before presenting it. Provide the FULL new markdown content for each section you are changing, keyed by section key (agent_name, agent_role_and_personality, caller_context, conversation_flow, behavior_and_guardrails); omit sections you are not changing. When set, checks run against these proposals overlaid on the saved draft, and the editor surfaces them inline as a suggestion (diff + Apply/Discard). Omit entirely to check the current saved draft."
  )
});
const getAgentInstructionChecksToolOutputSchema = z.object({
  candidate_draft: agentInstructionSectionCandidateDraftSchema.nullable(),
  checks: agentInstructionChecksLlmOutputSchema
});
const AGENT_INSTRUCTION_SECTION_CHECK_LABELS = {
  identity: {
    name_and_role_clear: "Name and role are clear",
    tone_and_personality_clear: "Tone and personality are clear"
  },
  caller_context: {
    caller_persona_defined: "Caller persona is clearly defined",
    consistent_with_conversation_flow: "Consistent with conversation flow"
  },
  conversation_flow: {
    opening_clear: "Opening is clear",
    steps_well_sequenced: "Steps are well sequenced",
    ending_paths_defined: "Ending paths are defined",
    instructions_consistent: "Instructions are consistent",
    tool_usage_clear: "Tool usage is clear"
  },
  behavior_and_guardrails: {
    boundaries_defined: "Boundaries are defined",
    unexpected_scenarios_handled: "Unexpected scenarios are handled",
    guardrails_consistent: "Guardrails are consistent",
    instructions_not_duplicated: "Instructions are not duplicated"
  }
};
const testSignalProposalToolInputSchema = z.object({
  candidate_name: z.string().min(1).optional().describe(
    "The proposed signal name to test. If omitted, uses the current signal name."
  ),
  candidate_description: z.string().min(1).optional().describe(
    "The proposed signal description to test. If omitted, uses the current signal description."
  ),
  candidate_metadata_schema: z.array(
    z.object({
      // The field ID may be omitted for brand-new fields the LLM is
      // proposing. When it references an existing field it should include
      // the same `id` that was given in the prompt.
      id: z.uuid().optional(),
      key: z.string().min(1),
      type: z.enum(["enum", "number"]),
      options: z.array(z.string()).optional()
    })
  ).nullable().optional().describe(
    "Optional metadata schema for the proposed signal. If omitted, uses the current signal metadata schema."
  )
});
const signalProposalMatchSchema = z.object({
  interaction_id: z.uuid(),
  contact_name: z.string().nullable(),
  teammate_name: z.string(),
  playbook_name: z.string(),
  matches: z.array(
    z.object({
      recording_id: recordingIdSchema,
      recorded_at: z.string().nullable(),
      explanation: z.string(),
      analysis: z.array(paragraphSchema),
      extracted_metadata: z.record(z.string(), z.union([z.string(), z.number(), z.null()])).nullable()
    })
  )
});
const testSignalProposalToolOutputSchema = z.object({
  interactions_with_matches: z.array(signalProposalMatchSchema),
  summary: z.object({
    total_interactions: z.number(),
    matched_interactions: z.number(),
    total_matches: z.number()
  })
});
const getSignalDetailsToolInputSchema = z.object({
  signal_id: z.uuid().describe("The signal ID to fetch details for.")
});
const getSignalDetailsToolOutputSchema = z.object({
  signal: z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    metadata_schema: z.array(
      z.object({
        id: metadataFieldIdSchema,
        key: z.string(),
        type: z.enum(["enum", "number"]),
        options: z.array(z.string()).optional()
      })
    ).nullable(),
    total_matches: z.number()
  }).nullable().describe("Signal details or null if not found")
});
export {
  AGENT_INSTRUCTION_SECTION_CHECK_LABELS,
  CONFIGURATION_AGENT_ID,
  KNOWLEDGE_BASE_AGENT_ID,
  LEARNING_JOURNEY_CONFIGURATION_AGENT_ID,
  SIGNAL_CONFIGURATION_AGENT_ID,
  STATIC_AGENT_SECTION_LABELS,
  agentInstructionChecksLlmOutputSchema,
  agentInstructionSectionCandidateDraftSchema,
  criteriaTypeSchema,
  criterionDraftChangesNode,
  criterionProposalDraftForSamplingSchema,
  criterionProposalDraftSchema,
  getAgentInstructionChecksToolInputSchema,
  getAgentInstructionChecksToolOutputSchema,
  getCustomerInteractionToolInputSchema,
  getCustomerInteractionToolOutputSchema,
  getPlaybookConfigurationInstructionsToolInputSchema,
  getPlaybookConfigurationInstructionsToolOutputSchema,
  getSampleCriteriaScoreDistributionAndOutcomeCorrelationToolInputSchema,
  getSampleCriteriaScoreDistributionAndOutcomeCorrelationToolOutputSchema,
  getSampleSectionApplicabilityToolInputSchema,
  getSampleSectionApplicabilityToolOutputSchema,
  getSignalDetailsToolInputSchema,
  getSignalDetailsToolOutputSchema,
  optionalUuidToolFieldSchema,
  outcomeDraftChangesRubric,
  outcomeProposalDraftSchema,
  playbookCriteriaHealthCheckLlmOutputSchema,
  playbookNodeTypeSchema,
  playbookOutcomeHealthCheckLlmOutputSchema,
  playbookProposalActionSchema,
  playbookProposalResultSchema,
  playbookSectionHealthCheckLlmOutputSchema,
  proposePlaybookNodeToolInputSchema,
  proposePlaybookNodeToolOutputSchema,
  proposePlaybookOutcomeToolInputSchema,
  proposePlaybookOutcomeToolOutputSchema,
  sampleCriteriaScoreDistributionTargetTypeSchema,
  sampleSectionApplicabilityTargetTypeSchema,
  sectionDraftChangesNode,
  sectionProposalDraftSchema,
  simulateAgentConversationToolInputSchema,
  simulateAgentConversationToolOutputSchema,
  simulateRolePlayRealismToolInputSchema,
  simulateRolePlayRealismToolOutputSchema,
  simulatedRolePlayAssessmentFindingSchema,
  simulatedRolePlayAssessmentSchema,
  testCriterionProposalTargetTypeSchema,
  testCriterionProposalToolInputSchema,
  testCriterionProposalToolOutputSchema,
  testSignalProposalToolInputSchema,
  testSignalProposalToolOutputSchema
};
//# sourceMappingURL=configurationAgent.js.map
